import { defineStore } from 'pinia'
import { store } from '../index'
import { UserLoginType, UserType } from '@/api/login/types'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useI18n } from '@/hooks/web/useI18n'
import { useTagsViewStore } from './tagsView'
import router from '@/router'
import { securePiniaStorage } from '@/utils/securePinia'
import { useAppStore } from './app'
import { usePermissionStore } from './permission'
import { Router, RouteRecordRaw } from 'vue-router'

interface UserState {
  userInfo?: UserType
  tokenKey: string
  token: string
  roleRouters?: string[] | AppCustomRouteRecordRaw[]
  rememberMe: boolean
  loginInfo?: UserLoginType
  tenantId: string | null 
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      userInfo: undefined,
      tokenKey: 'Authorization',
      token: '',
      roleRouters: undefined,
      // 記住我
      rememberMe: true,
      loginInfo: undefined,
      tenantId: null
    }
  },
  getters: {
    getTokenKey(): string {
      return this.tokenKey
    },
    getToken(): string {
      return this.token
    },
    getUserInfo(): UserType | undefined {
      return this.userInfo
    },
    getRoleRouters(): string[] | AppCustomRouteRecordRaw[] | undefined {
      return this.roleRouters
    },
    getRememberMe(): boolean {
      return this.rememberMe
    },
    getLoginInfo(): UserLoginType | undefined {
      return this.loginInfo
    },
    isSuperAdmin(): boolean {
      return !this.tenantId
    }
  },
  actions: {
    setTokenKey(tokenKey: string) {
      this.tokenKey = tokenKey
    },
    setToken(token: string) {
      this.token = token
    },
    setUserInfo(userInfo?: UserType) {
      this.userInfo = userInfo
      if (userInfo) {
        this.setToken(userInfo.access_token)
        this.tenantId = userInfo.tenantId || null
      }
    },
    setRoleRouters(roleRouters: string[] | AppCustomRouteRecordRaw[]) {
      this.roleRouters = roleRouters
    },
    checkExpires(): boolean {
      if (!this.userInfo || !this.userInfo.expires) {
        return true
      }
      const currentTime = new Date()
      if (new Date(this.userInfo.expires) <= currentTime) {
        const { t } = useI18n()
        const error = t('common.sessionExpired')
        ElMessage({
          dangerouslyUseHTMLString: true,
          message: `<strong>${error}</strong>`,
          type: 'error'
        })
        this.logout()
        return true
      }
      return false
    },
    logoutConfirm() {
      const { t } = useI18n()

      // 確保在下一個事件循環中執行，避免 DOM 狀態衝突
      setTimeout(() => {
        ElMessageBox.confirm(t('common.loginOutMessage'), t('common.reminder'), {
          confirmButtonText: t('common.ok'),
          cancelButtonText: t('common.cancel'),
          type: 'warning',
          lockScroll: true,
          showClose: true,
          closeOnClickModal: false,
          closeOnPressEscape: true,
          // 增加 z-index 確保視窗在最上層
          customClass: 'logout-confirm-dialog'
        })
          .then(async () => {
            // const res = await loginOutApi().catch(() => {})
            // if (res) {
            //   this.reset()
            // }
            this.reset()
          })
          .catch((error) => {
            // 只有非取消操作的錯誤才需要處理
            if (error !== 'cancel' && error !== 'close') {
              console.warn('登出確認視窗錯誤:', error)
              // 提供備用確認方式
              if (confirm('確定要登出嗎？')) {
                this.reset()
              }
            }
          })
      }, 10) // 短暫延遲確保 DOM 狀態穩定
    },
    reset() {
      const tagsViewStore = useTagsViewStore()
      tagsViewStore.delAllViews()
      this.setToken('')
      this.setUserInfo(undefined)
      this.setRoleRouters([])
      router.replace('/login')
    },
    logout() {
      this.reset()
    },
    logoutAsync() {
      return new Promise<void>((resolve) => {
        this.reset()
        resolve()
      })
    },
    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe
    },
    setLoginInfo(loginInfo: UserLoginType | undefined) {
      this.loginInfo = loginInfo
    },
    async initInfo(router: Router) {
      const appStore = useAppStore()
      const permissionStore = usePermissionStore()

      const routers = permissionStore.serverRouters || []

      this.setRoleRouters(routers)

      appStore.getDynamicRouter && appStore.getServerDynamicRouter
        ? await permissionStore.generateRoutes('server', routers).catch(() => {})
        : await permissionStore.generateRoutes('frontEnd', routers).catch(() => {})

      permissionStore.getAddRouters.forEach((route) => {
        router.addRoute(route as RouteRecordRaw) // 動態添加可訪問路由表
      })

      if (permissionStore.getIsAddRouters) {
        permissionStore.setIsAddRouters(true)
      }
    }
  },
  persist: {
    key: `${securePiniaStorage.prefix('user')}`,
    storage: securePiniaStorage.storage
  }
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
